import { useEffect, useState } from 'react';
import { useCurrentUser } from './useCurrentUser';
import { useSubscriptionStatus } from './useSubscriptionStatus';

// Who is looking at the public plan UI, and which button each plan gets for them. Shared by
// the /pricing top plan cards and PricingCompare's Yearly/Monthly boxes (also on the homepage),
// so the two always agree.
//
// Viewer keys: 'guest' (logged out, still loading, or a state we can't place: keep the default
// sign-up CTAs), 'trial' (trialing, chosen plan unknown), 'trialMonthly' / 'trialYearly'
// (trialing with that plan set to start after the trial), 'monthly', 'yearly'.
//
// Button kinds:
//   current  - white box, not clickable (the plan they're on, or the one starting after trial)
//   expired  - black outline, no fill, not clickable (the trial, once it's behind them)
//   upgrade  - a link to /subscribe?plan=... for the other plan
export const PLAN_CTAS = {
    trial: {
        trial: { kind: 'current', label: 'Currently under trial' },
        yearly: { kind: 'upgrade', label: 'Upgrade to yearly plan', href: '/subscribe?plan=yearly' },
        monthly: { kind: 'upgrade', label: 'Upgrade to monthly plan', href: '/subscribe?plan=monthly' },
    },
    trialMonthly: {
        trial: { kind: 'current', label: 'Currently under trial' },
        yearly: { kind: 'upgrade', label: 'Switch to yearly plan', href: '/subscribe?plan=yearly' },
        monthly: { kind: 'current', label: 'Starts after trial' },
    },
    trialYearly: {
        trial: { kind: 'current', label: 'Currently under trial' },
        yearly: { kind: 'current', label: 'Starts after trial' },
        monthly: { kind: 'upgrade', label: 'Switch to monthly plan', href: '/subscribe?plan=monthly' },
    },
    monthly: {
        trial: { kind: 'expired', label: 'Trial expired' },
        yearly: { kind: 'upgrade', label: 'Upgrade to yearly', href: '/subscribe?plan=yearly' },
        monthly: { kind: 'current', label: 'Current plan' },
    },
    yearly: {
        trial: { kind: 'expired', label: 'Trial expired' },
        yearly: { kind: 'current', label: 'Current plan' },
        monthly: { kind: 'upgrade', label: 'Downgrade to monthly', href: '/subscribe?plan=monthly' },
    },
};

// Maps the subscription to a PLAN_CTAS key. A paying status with an unknown interval falls
// back to 'guest' rather than guessing which plan to mark as current.
export const getViewerPlan = ({ status, interval }) => {
    if (status === 'trialing') {
        if (interval === 'year') return 'trialYearly';
        if (interval === 'month') return 'trialMonthly';
        return 'trial';
    }
    if (status === 'active' || status === 'past_due') {
        if (interval === 'year') return 'yearly';
        if (interval === 'month') return 'monthly';
    }
    return 'guest';
};

// Returns the viewer key. Always 'guest' on the server and on the first client render (no
// user during SSR, so this keeps hydration consistent), then the real state once mounted.
// The subscription request only fires for logged-in viewers.
export const usePlanViewer = () => {
    const [currentUser] = useCurrentUser();
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => { setHasMounted(true); }, []);

    const isLoggedIn = hasMounted && !!currentUser;
    const { subscriptionData, status, interval } = useSubscriptionStatus({ enabled: isLoggedIn });

    if (!isLoggedIn || !subscriptionData) return 'guest';
    return getViewerPlan({ status, interval });
};
